import type { SegmentationPreview } from "./segmentationPreviews";
import { decodePredictionMaskRuns } from "./segmentationMask";
import { presetForFinding } from "../utils/findingWindow";

export interface PreviewWindow {
  windowWidth: number;
  windowCenter: number;
}

const MASK_BORDER = "#a855f7";
// Matches the selected-card border (`border-signal`).
const AXIS_LONG = "#4361ee";
const AXIS_SHORT = "#93b4ff";

function mapPoint(
  point: [number, number],
  crop: SegmentationPreview["cropBoundsPx"],
  width: number,
  height: number,
): [number, number] {
  return [((point[0] - crop.x) / crop.w) * width, ((point[1] - crop.y) / crop.h) * height];
}

function drawMaskBorder(
  ctx: CanvasRenderingContext2D,
  preview: SegmentationPreview,
  sourceWidth: number,
  sourceHeight: number,
  outputWidth: number,
  outputHeight: number,
): void {
  const bitmap = decodePredictionMaskRuns(preview.maskRows, preview.maskColumns, preview.maskRuns);
  const crop = preview.cropBoundsPx;
  const sourceXScale = sourceWidth / preview.maskColumns;
  const sourceYScale = sourceHeight / preview.maskRows;
  const toOutput = (column: number, row: number): [number, number] => [
    ((column * sourceXScale - crop.x) / crop.w) * outputWidth,
    ((row * sourceYScale - crop.y) / crop.h) * outputHeight,
  ];
  const isSet = (row: number, column: number) =>
    row >= 0 &&
    row < preview.maskRows &&
    column >= 0 &&
    column < preview.maskColumns &&
    bitmap[row * preview.maskColumns + column] === 1;

  ctx.save();
  ctx.beginPath();
  for (let index = 0; index < bitmap.length; index++) {
    if (!bitmap[index]) continue;
    const row = Math.floor(index / preview.maskColumns);
    const column = index % preview.maskColumns;
    const topLeft = toOutput(column, row);
    const topRight = toOutput(column + 1, row);
    const bottomRight = toOutput(column + 1, row + 1);
    const bottomLeft = toOutput(column, row + 1);
    if (!isSet(row - 1, column)) {
      ctx.moveTo(...topLeft);
      ctx.lineTo(...topRight);
    }
    if (!isSet(row, column + 1)) {
      ctx.moveTo(...topRight);
      ctx.lineTo(...bottomRight);
    }
    if (!isSet(row + 1, column)) {
      ctx.moveTo(...bottomRight);
      ctx.lineTo(...bottomLeft);
    }
    if (!isSet(row, column - 1)) {
      ctx.moveTo(...bottomLeft);
      ctx.lineTo(...topLeft);
    }
  }
  ctx.strokeStyle = MASK_BORDER;
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.restore();
}

export async function renderSegmentationPreviewThumbnail(
  preview: SegmentationPreview,
  loadFrame: (
    seriesUid: string,
    sliceIndex: number,
    window: PreviewWindow,
  ) => Promise<HTMLCanvasElement>,
  size = { w: 132, h: 132 },
): Promise<HTMLCanvasElement> {
  const preset = presetForFinding(preview.condition);
  const window = { windowWidth: preset.width, windowCenter: preset.center };
  const src = await loadFrame(preview.seriesInstanceUid, preview.sliceIndex, window);
  const out = document.createElement("canvas");
  out.width = size.w;
  out.height = size.h;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable for segmentation preview thumbnail");
  const crop = preview.cropBoundsPx;
  ctx.drawImage(src, crop.x, crop.y, crop.w, crop.h, 0, 0, size.w, size.h);
  drawMaskBorder(ctx, preview, src.width, src.height, size.w, size.h);

  const long = preview.longAxisPx.map((point) => mapPoint(point, crop, size.w, size.h));
  const short = preview.shortAxisPx.map((point) => mapPoint(point, crop, size.w, size.h));
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = AXIS_LONG;
  ctx.beginPath();
  ctx.moveTo(...long[0]);
  ctx.lineTo(...long[1]);
  ctx.stroke();
  ctx.strokeStyle = AXIS_SHORT;
  ctx.setLineDash([2.5, 2]);
  ctx.beginPath();
  ctx.moveTo(...short[0]);
  ctx.lineTo(...short[1]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.font = "8px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.textBaseline = "top";
  ctx.fillText(`Seg ${preview.segmentIndex} · Sl ${preview.sliceNumber}`, 4, 4);
  ctx.textBaseline = "bottom";
  ctx.textAlign = "right";
  ctx.fillText(`${preview.voxelCount} vox`, size.w - 4, size.h - 4);
  return out;
}
